import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Copy, Check, Flame, ArrowRight, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { CAMPAIGN } from '../../config/promoCampaign';
import api from '../../services/api';

/**
 * Bannière de campagne promotionnelle — 100% pilotée par /config/promoCampaign.js.
 * Réutilisable pour toute future campagne (dates/montants/texte/code) sans
 * modifier ce composant.
 *
 * Le nombre d'accès restants est connecté en LIVE à GET /api/promo-campaign/status
 * (utilisations réelles du code promo, `use_count` sur promo_codes_unique) —
 * la config `spotsTotal`/`spotsRemaining` ne sert plus que de valeur de repli
 * si l'API est momentanément indisponible.
 *
 * Aide QA / prévisualisation (sans attendre les vraies dates) : ajouter
 * ?promo_preview=before|phase1|phase2|after à l'URL de l'accueil.
 */

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const getPreviewParam = () => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('promo_preview');
};

function resolveCampaignState(campaign, tickNow) {
  const phases = campaign.phases.map((p) => ({
    ...p,
    startMs: new Date(p.start).getTime(),
    endMs: new Date(p.end).getTime(),
  }));
  const preview = getPreviewParam();

  if (preview === 'before') {
    const fakeNow = phases[0].startMs - (3 * 86400000 + 4 * 3600000 + 12 * 60000);
    return { status: 'before', phase: null, target: phases[0].startMs, nowMs: fakeNow };
  }
  if (preview === 'after') {
    return { status: 'ended', phase: null, target: null, nowMs: tickNow };
  }
  if (preview === 'phase1' && phases[0]) {
    return { status: 'active', phase: phases[0], target: phases[0].endMs, nowMs: tickNow };
  }
  if (preview === 'phase2' && phases[1]) {
    return { status: 'active', phase: phases[1], target: phases[1].endMs, nowMs: tickNow };
  }

  const active = phases.find((p) => tickNow >= p.startMs && tickNow <= p.endMs);
  if (active) return { status: 'active', phase: active, target: active.endMs, nowMs: tickNow };
  if (tickNow < phases[0].startMs) return { status: 'before', phase: null, target: phases[0].startMs, nowMs: tickNow };
  return { status: 'ended', phase: null, target: null, nowMs: tickNow };
}

export const PromoCampaignBanner = () => {
  const [tick, setTick] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null); // { active, phase, use_count, max_uses, remaining }

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get('/promo-campaign/status').then((r) => setLiveStatus(r.data)).catch(() => {});
  }, []);

  const state = useMemo(() => resolveCampaignState(CAMPAIGN, tick), [tick]);

  if (!CAMPAIGN.enabled || state.status === 'ended') return null;

  const diff = state.target ? Math.max(0, state.target - state.nowMs) : 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  const isBefore = state.status === 'before';
  const phase = state.phase;
  // N'applique les chiffres LIVE (utilisations réelles) que si le statut backend
  // correspond bien à la phase actuellement affichée (évite un décalage en mode preview QA).
  const liveMatchesPhase = liveStatus?.active && phase && liveStatus.phase === phase.id;
  const spotsTotal = liveMatchesPhase ? liveStatus.max_uses : phase?.spotsTotal;
  const spotsRemaining = liveMatchesPhase ? liveStatus.remaining : phase?.spotsRemaining;
  const spotsPct = phase ? Math.max(0, Math.min(100, (spotsRemaining / spotsTotal) * 100)) : 0;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(CAMPAIGN.promoCode);
      setCopied(true);
      toast.success('Code copi\u00e9 !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le code');
    }
  };

  return (
    <section
      className="relative overflow-hidden border-b border-white/10"
      style={{
        background:
          'radial-gradient(ellipse 90% 60% at 50% -10%, rgba(0,255,136,0.10), transparent), linear-gradient(180deg, #060907 0%, #0A0A0B 60%, #0A0A0B 100%)',
      }}
      data-testid="promo-campaign-banner"
    >
      {/* Halo lumineux derrière le titre */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[280px] bg-[#FF6B35]/20 blur-[100px] rounded-full" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
            {!isBefore && (
              <span
                className="inline-flex items-center gap-1.5 bg-[#FF3B5C]/15 border border-[#FF3B5C]/40 text-[#FF3B5C] text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                data-testid="promo-badge-limited"
              >
                <Flame className="h-3 w-3 animate-pulse" /> Offre limitée
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 text-[#B8B8BA] text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
              <CalendarClock className="h-3 w-3 text-[#00FF88]" /> Reprise le {CAMPAIGN.eventDateLabel}
            </span>
          </div>

          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="text-3xl sm:text-4xl">{CAMPAIGN.eventEmoji}</span>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight"
              style={{ textShadow: '0 0 40px rgba(255,107,53,0.45)' }}
              data-testid="promo-title"
            >
              {CAMPAIGN.eventTitle}
            </h2>
          </div>
          {CAMPAIGN.subtitleLines.map((line, i) => (
            <p key={i} className={`text-sm sm:text-base ${i === 0 ? 'text-[#E8E8E8]/90 font-medium' : 'text-[#B8B8BA]'}`}>
              {line}
            </p>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 max-w-3xl mx-auto"
        >
          {/* Carte offre */}
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 backdrop-blur-sm" data-testid="promo-offer-card">
            {isBefore ? (
              <>
                <p className="text-[#6B6B6E] text-xs font-semibold uppercase tracking-wider mb-2">Offre à venir</p>
                <p className="text-white font-bold text-lg">Jusqu'à -{CAMPAIGN.phases[0].discountEuros}&euro;</p>
                <p className="text-[#6B6B6E] text-xs mt-1">Dévoilée au lancement de l'offre</p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#6B6B6E] text-xs font-semibold uppercase tracking-wider">{phase.label}</p>
                  <span className="text-[#00FF88] text-[10px] font-bold bg-[#00FF88]/10 border border-[#00FF88]/30 px-2 py-0.5 rounded-full">
                    EN COURS
                  </span>
                </div>
                <p
                  className="text-4xl font-extrabold bg-gradient-to-r from-[#FF6B35] to-[#F7931E] bg-clip-text text-transparent"
                  data-testid="promo-discount-amount"
                >
                  -{phase.discountEuros}&euro;
                </p>
                <p className="text-[#B8B8BA] text-xs mt-1">avec le code promo ci-dessous</p>
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[#B8B8BA] text-xs">Accès restants</span>
                    <span className="text-white text-xs font-bold" data-testid="promo-spots-remaining">
                      {spotsRemaining}/{spotsTotal}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF3B5C] to-[#FF6B35] rounded-full transition-all duration-700"
                      style={{ width: `${spotsPct}%` }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Carte compte à rebours */}
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 backdrop-blur-sm" data-testid="promo-countdown-card">
            <p className="text-[#6B6B6E] text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {isBefore ? "L'offre démarre dans" : `${phase.label} se termine dans`}
            </p>
            <div className="grid grid-cols-4 gap-1.5" data-testid="promo-countdown-timer">
              {[{ v: days, l: 'j' }, { v: hours, l: 'h' }, { v: mins, l: 'min' }, { v: secs, l: 's' }].map((seg, i) => (
                <div key={i} className="text-center bg-black/30 rounded-lg py-2 border border-white/5">
                  <div className="text-white font-bold text-lg sm:text-xl tabular-nums">{pad(seg.v)}</div>
                  <div className="text-[#6B6B6E] text-[10px] uppercase">{seg.l}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col items-center mt-6"
        >
          <p className="text-[#6B6B6E] text-xs mb-2">{isBefore ? 'Le code sera révélé au lancement' : 'Votre code promo :'}</p>
          <button
            onClick={!isBefore ? copyCode : undefined}
            disabled={isBefore}
            className={`inline-flex items-center gap-2.5 bg-[#1A1A1D] border-2 border-dashed rounded-xl px-5 py-2.5 transition-colors ${
              isBefore ? 'border-white/10 opacity-50 cursor-not-allowed' : 'border-[#FF6B35]/50 hover:border-[#FF6B35]'
            }`}
            data-testid="promo-code-copy"
          >
            <span className="font-mono font-bold text-white text-base sm:text-lg tracking-wider">
              {isBefore ? '••••••••••' : CAMPAIGN.promoCode}
            </span>
            {!isBefore && (copied ? <Check className="h-4 w-4 text-[#00FF88]" /> : <Copy className="h-4 w-4 text-[#6B6B6E]" />)}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-7"
        >
          <Link
            to={`${CAMPAIGN.ctaPrimary.to}?promo=${encodeURIComponent(CAMPAIGN.promoCode)}`}
            className="w-full sm:w-auto bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold px-8 py-3.5 rounded-lg hover:shadow-2xl hover:shadow-[#FF6B35]/40 transition-all duration-300 inline-flex items-center justify-center gap-2"
            data-testid="promo-cta-primary"
          >
            {CAMPAIGN.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to={CAMPAIGN.ctaSecondary.to}
            className="w-full sm:w-auto bg-white/5 text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-white/10 transition-all duration-300 border border-white/15 inline-flex items-center justify-center"
            data-testid="promo-cta-secondary"
          >
            {CAMPAIGN.ctaSecondary.label}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default PromoCampaignBanner;
